// Web Bluetooth reader for BP cuffs (0x1810) & glucose meters (0x1808).
// Parses the standard GATT Blood Pressure Measurement (0x2A35) and Glucose
// Measurement (0x2A18) characteristics. Values are IEEE 11073-20601 SFLOAT.
// Result shape: { systolic, diastolic, pulse, glucose, unit, timestamp }.
// Only fields the device actually broadcast are populated — the caller
// then patches the vitals form.

// ─── IEEE 11073-20601 SFLOAT decoder ──────────────────────
// 16-bit: 4-bit signed exponent + 12-bit signed mantissa
function decodeSFLOAT(view, offset, little = true) {
  const raw = view.getUint16(offset, little);
  let mantissa = raw & 0x0fff;
  let exponent = raw >> 12;
  if (exponent >= 0x8) exponent = -((0x0f + 1) - exponent);
  if (mantissa >= 0x0800) mantissa = -((0x0fff + 1) - mantissa);
  // Reserved values (NRes, +INFINITY, NaN, -INFINITY, NRes)
  if (raw === 0x07ff || raw === 0x0800 || raw === 0x0801 ||
      raw === 0x0802 || raw === 0x07fe) return null;
  return mantissa * Math.pow(10, exponent);
}

// ─── Blood Pressure Measurement (0x2A35) ──────────────────
// Byte 0: flags (bit 0: units — 0=mmHg, 1=kPa; bit 1: timestamp present;
//          bit 2: pulse present)
// Bytes 1-2: systolic (SFLOAT)
// Bytes 3-4: diastolic (SFLOAT)
// Bytes 5-6: MAP (SFLOAT)
// [Bytes 7-13 if bit 1: timestamp]
// [Bytes N-N+1 if bit 2: pulse (SFLOAT)]
function parseBP(data) {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const flags = view.getUint8(0);
  const kPa = (flags & 0x01) !== 0;
  const hasTimestamp = (flags & 0x02) !== 0;
  const hasPulse = (flags & 0x04) !== 0;
  const sys = decodeSFLOAT(view, 1);
  const dia = decodeSFLOAT(view, 3);
  // MAP at 5-6 — skip.
  let off = 7;
  let timestamp = null;
  if (hasTimestamp) {
    const yr = view.getUint16(off, true);
    const mo = view.getUint8(off + 2);
    const dy = view.getUint8(off + 3);
    const hr = view.getUint8(off + 4);
    const mn = view.getUint8(off + 5);
    const sc = view.getUint8(off + 6);
    timestamp = new Date(yr, (mo || 1) - 1, dy || 1, hr, mn, sc).toISOString();
    off += 7;
  }
  let pulse = null;
  if (hasPulse) pulse = decodeSFLOAT(view, off);
  const toMmHg = (v) => (v == null ? null : kPa ? Math.round(v * 7.50062) : Math.round(v));
  return {
    systolic: toMmHg(sys),
    diastolic: toMmHg(dia),
    pulse: pulse == null ? null : Math.round(pulse),
    unit: "mmHg",
    timestamp,
  };
}

// ─── Glucose Measurement (0x2A18) ─────────────────────────
// Byte 0: flags (bit 0: time offset present; bit 1: concentration+type present;
//                bit 2: units — 0=kg/L (mg/dL after scale), 1=mol/L (mmol/L))
// Bytes 1-2: sequence number
// Bytes 3-9: base time
// [Bytes 10-11: time offset]
// [Bytes N-N+1: concentration (SFLOAT), Byte N+2: type/location nibble]
function parseGlucose(data) {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const flags = view.getUint8(0);
  const hasOffset = (flags & 0x01) !== 0;
  const hasConcentration = (flags & 0x02) !== 0;
  const mmol = (flags & 0x04) !== 0;
  // Base time (7 bytes)
  const yr = view.getUint16(3, true);
  const mo = view.getUint8(5);
  const dy = view.getUint8(6);
  const hr = view.getUint8(7);
  const mn = view.getUint8(8);
  const sc = view.getUint8(9);
  const timestamp = new Date(yr, (mo || 1) - 1, dy || 1, hr, mn, sc).toISOString();
  let off = 10;
  if (hasOffset) off += 2;
  let glucose = null;
  if (hasConcentration) {
    const raw = decodeSFLOAT(view, off);
    if (raw != null) {
      // kg/L → multiply by 100000 to get mg/dL; mol/L → multiply by 1000 for mmol/L
      // Some meters already broadcast mg/dL directly with kg/L flag — heuristic:
      // if raw < 1 assume kg/L scaling, else assume already mg/dL.
      if (mmol) glucose = Math.round(raw * 1000 * 10) / 10; // mmol/L
      else glucose = raw < 1 ? Math.round(raw * 100000) : Math.round(raw);
    }
  }
  return {
    glucose,
    unit: mmol ? "mmol/L" : "mg/dL",
    timestamp,
  };
}

// ─── Public API ───────────────────────────────────────────
export const bluetoothSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

export async function readBPCuff() {
  if (!bluetoothSupported()) throw new Error("Web Bluetooth is not available in this browser. Open on Chrome/Edge on desktop or Android.");
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: ["blood_pressure"] }],
    optionalServices: ["device_information"],
  });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService("blood_pressure");
  const ch = await service.getCharacteristic("blood_pressure_measurement");
  // BP Measurement is Indicate, not Read — wait one indication.
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Timed out waiting for cuff. Start a measurement now.")), 60000);
    const onIndicate = (evt) => {
      clearTimeout(timer);
      try {
        const parsed = parseBP(evt.target.value);
        ch.removeEventListener("characteristicvaluechanged", onIndicate);
        server.disconnect();
        resolve({ ...parsed, device_name: device.name || "BP Cuff" });
      } catch (e) { reject(e); }
    };
    ch.addEventListener("characteristicvaluechanged", onIndicate);
    ch.startNotifications().catch(reject);
  });
}

export async function readGlucoseMeter() {
  if (!bluetoothSupported()) throw new Error("Web Bluetooth is not available in this browser. Open on Chrome/Edge on desktop or Android.");
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: ["glucose"] }],
    optionalServices: ["device_information"],
  });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService("glucose");
  const ch = await service.getCharacteristic("glucose_measurement");
  const ctrl = await service.getCharacteristic("record_access_control_point").catch(() => null);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Timed out waiting for meter reading.")), 60000);
    let latest = null;
    ch.addEventListener("characteristicvaluechanged", (evt) => {
      try { latest = parseGlucose(evt.target.value); } catch (e) { /* keep waiting */ }
    });
    ch.startNotifications().then(() => {
      // Ask meter for the single most-recent record.
      // Op code 0x01 (Report Records), operator 0x06 (last record).
      if (ctrl) {
        ctrl.startNotifications().then(() => {
          ctrl.addEventListener("characteristicvaluechanged", () => {
            clearTimeout(timer);
            server.disconnect();
            if (latest && latest.glucose != null) {
              resolve({ ...latest, device_name: device.name || "Glucose Meter" });
            } else {
              reject(new Error("No reading found on meter. Take a measurement then try again."));
            }
          });
          ctrl.writeValueWithoutResponse(new Uint8Array([0x01, 0x06])).catch(reject);
        }).catch(reject);
      }
    }).catch(reject);
  });
}
